import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { ShoppingBag, Package, Users, DollarSign, Clock, CheckCircle2, Search, Play } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p);

const statusLabels = {
  pending: 'Pendiente', approved: 'Aprobado', production: 'Producción', delivery: 'Entrega', delivered: 'Entregado', cancelled: 'Cancelado'
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchCode, setSearchCode] = useState('');

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/dashboard`, { withCredentials: true, headers: getHeaders() });
      setStats(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPendingOrders = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/orders?status=pending`, { withCredentials: true, headers: getHeaders() });
      setPendingOrders(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchPendingOrders()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchCode.trim()) {
      navigate(`/admin/pedidos?search=${encodeURIComponent(searchCode.trim())}`);
    }
  };

  const handleApprove = async (orderId) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status: 'production' }, { withCredentials: true, headers: getHeaders() });
      toast.success('Pedido aprobado y enviado a producción automáticamente');
      fetchPendingOrders();
      fetchStats();
    } catch (err) {
      console.error(err);
      toast.error('Error al aprobar el pedido');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" /></div>;

  const statCards = [
    { label: 'Total Pedidos', value: stats?.total_orders || 0, icon: ShoppingBag, color: 'bg-[#a0d5ba]/20 text-[#46371c]' },
    { label: 'Pendientes', value: stats?.pending_orders || 0, icon: Clock, color: 'bg-yellow-50 text-yellow-700' },
    { label: 'Productos', value: stats?.total_products || 0, icon: Package, color: 'bg-blue-50 text-blue-700' },
    { label: 'Clientes', value: stats?.total_clients || 0, icon: Users, color: 'bg-purple-50 text-purple-700' },
    { label: 'Ingresos', value: formatPrice(stats?.total_revenue || 0), icon: DollarSign, color: 'bg-green-50 text-green-700' },
    { label: 'Entregados', value: stats?.delivered || 0, icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-700' },
  ];

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#46371c]">Dashboard</h1>
          <p className="font-body text-sm text-[#75664d] mt-1">Bienvenido, {user?.name}</p>
        </div>
        
        {/* Search Order Bar */}
        <form onSubmit={handleSearchSubmit} className="flex gap-2 max-w-sm w-full">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#75664d]" />
            <input
              type="text"
              placeholder="Buscar orden por código..."
              value={searchCode}
              onChange={e => setSearchCode(e.target.value)}
              className="w-full h-10 pl-9 pr-4 rounded-xl border border-[#e5dfd3] bg-[#fcfbf9] text-xs font-body text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
            />
          </div>
          <Button type="submit" size="sm" className="bg-[#46371c] hover:bg-[#2d2312] text-white rounded-xl">Buscar</Button>
        </form>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="dashboard-stats">
        {statCards.map(stat => (
          <Card key={stat.label} className="border border-[#e5dfd3] bg-[#fcfbf9]">
            <CardContent className="p-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${stat.color}`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <p className="font-body text-xs text-[#75664d]">{stat.label}</p>
              <p className="font-heading text-2xl font-bold text-[#46371c]">{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Approvals Section */}
        <Card className="border border-[#e5dfd3] bg-[#fcfbf9]">
          <div className="p-6 border-b border-[#e5dfd3] flex justify-between items-center">
            <h2 className="font-heading text-lg font-semibold text-[#46371c] flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-600" /> Pedidos por Aprobar
            </h2>
            <Badge className="bg-yellow-100 text-yellow-800 border-none">{pendingOrders.length}</Badge>
          </div>
          <div className="p-6 max-h-[400px] overflow-y-auto space-y-3">
            {pendingOrders.length > 0 ? (
              pendingOrders.map(order => (
                <div key={order.id} className="flex items-center justify-between p-4 rounded-xl bg-[#f4f1ea] border border-[#e5dfd3]/40">
                  <div>
                    <p className="font-body text-sm font-semibold text-[#46371c]">#{order.tracking_code}</p>
                    <p className="font-body text-xs text-[#75664d]">{order.customer_name} ({order.customer_phone})</p>
                    <p className="font-body text-[10px] text-green-700 font-medium mt-0.5">{order.delivery_type === 'delivery' ? 'Domicilio' : 'Retiro en Tienda'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-body text-sm font-semibold text-[#46371c] mr-2">{formatPrice(order.total)}</span>
                    <Button
                      onClick={() => handleApprove(order.id)}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white rounded-xl text-xs flex items-center gap-1.5 px-3 py-1.5 h-8 font-body font-semibold"
                    >
                      <Play className="w-3.5 h-3.5" /> Aprobar
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="font-body text-sm text-[#75664d] text-center py-8">No hay pedidos pendientes por aprobar</p>
            )}
          </div>
        </Card>

        {/* Recent Orders */}
        <Card className="border border-[#e5dfd3] bg-[#fcfbf9]">
          <div className="p-6 border-b border-[#e5dfd3]">
            <h2 className="font-heading text-lg font-semibold text-[#46371c]">Pedidos Recientes</h2>
          </div>
          <div className="p-6 max-h-[400px] overflow-y-auto">
            {stats?.recent_orders?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_orders.map(order => (
                  <div key={order.id} className="flex items-center justify-between p-3 rounded-xl bg-[#f4f1ea]" data-testid={`recent-order-${order.tracking_code}`}>
                    <div>
                      <p className="font-body text-sm font-medium text-[#46371c]">#{order.tracking_code}</p>
                      <p className="font-body text-xs text-[#75664d]">{order.customer_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-body text-sm font-semibold text-[#46371c]">{formatPrice(order.total)}</p>
                      <Badge className={`text-xs ${order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : order.status === 'delivered' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {statusLabels[order.status] || order.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="font-body text-sm text-[#75664d] text-center py-8">No hay pedidos aun</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
